import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import AmbulanceManagement from './AmbulanceManagement';
import AmbulanceTypeManagement from './AmbulanceTypeManagement';
import GpsDeviceManagement from './GpsDeviceManagement';
import AreaManagement from './AreaManagement';
import StaffCategoryManagement from './StaffCategoryManagement';
import RoleManagement from './RoleManagement';
import PatientVitalFieldsManagement from './PatientVitalFieldsManagement';
import TenantSettings from './TenantSettings';
import ConfigSettings from './ConfigSettings';
import PermissionManagement from './PermissionManagement';
import Profile from './Profile';
import LoginPageConfig from './LoginPageConfig';
import CancelReasonManagement from './CancelReasonManagement';
import TariffManagement from './TariffManagement';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const Settings: React.FC = () => {
  const { hasPermission, isTenantLevelAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDevMode, setIsDevMode] = React.useState(false);

  const canSeeTenantTab =
    !isTenantLevelAdmin &&
    (hasPermission('tenant.list') ||
      hasPermission('tenant.get') ||
      hasPermission('tenant.update') ||
      hasPermission('tenant.create') ||
      hasPermission('tenant.delete'));
  
  // Get main tab from URL, default to 'ambulance'
  const mainTabFromUrl = searchParams.get('mainTab') as 'ambulance' | 'staff' | 'patient' | 'tenant' | 'booking' | null;
  const initialMainTab =
    mainTabFromUrl === 'tenant' && !canSeeTenantTab
      ? 'ambulance'
      : mainTabFromUrl && ['ambulance', 'staff', 'patient', 'tenant', 'booking'].includes(mainTabFromUrl)
        ? mainTabFromUrl
        : 'ambulance';
  const [mainTab, setMainTab] = useState<'ambulance' | 'staff' | 'patient' | 'tenant' | 'booking'>(initialMainTab);

  // Get sub tab from URL
  const subTabFromUrl = searchParams.get('subTab');
  const [subTab, setSubTab] = useState<string>(subTabFromUrl || '');

  // Update URL when tabs change
  React.useEffect(() => {
    const params: { mainTab: string; subTab?: string } = { mainTab };
    if (subTab) {
      params.subTab = subTab;
    }
    setSearchParams(params, { replace: true });
  }, [mainTab, subTab, setSearchParams]);

  // If user has no tenant permissions, leave the Tenant tab
  React.useEffect(() => {
    if (mainTab === 'tenant' && !canSeeTenantTab) {
      setMainTab('ambulance');
      setSubTab('ambulance-table');
    }
  }, [mainTab, canSeeTenantTab]);

  // Set default sub tab when main tab changes or on initial load
  React.useEffect(() => {
    if (!subTab || !isValidSubTab(mainTab, subTab)) {
      const defaultSubTab = getDefaultSubTab(mainTab);
      setSubTab(defaultSubTab);
    }
  }, [mainTab, subTab]);

  // Check for #dev mode
  React.useEffect(() => {
    const checkHash = () => {
      setIsDevMode(window.location.hash === '#dev');
    };

    checkHash();
    window.addEventListener('hashchange', checkHash);

    return () => {
      window.removeEventListener('hashchange', checkHash);
    };
  }, []);

  const isValidSubTab = (main: string, sub: string): boolean => {
    const validSubTabs: Record<string, string[]> = {
      ambulance: ['ambulance-table', 'ambulance-type', 'gps-devices', 'areas', 'tariff'],
      staff: ['staff-categories', 'roles'],
      patient: ['patient-vital-fields'],
      tenant: ['tenant-settings', 'config-settings', 'permissions', 'profile', ...(isDevMode ? ['login-page-config'] : [])],
      booking: ['cancel-reason'],
    };
    return validSubTabs[main]?.includes(sub) || false;
  };

  const getDefaultSubTab = (main: string): string => {
    const defaults: Record<string, string> = {
      ambulance: 'ambulance-table',
      staff: 'staff-categories',
      patient: 'patient-vital-fields',
      tenant: 'tenant-settings',
      booking: 'cancel-reason',
    };
    return defaults[main] || '';
  };

  const ambulanceSubTabs = [
    { id: 'ambulance-table', label: 'Ambulances', permission: 'ambulance.list' },
    { id: 'ambulance-type', label: 'Ambulance Type', permission: 'ambulancetype.list' },
    { id: 'gps-devices', label: 'GPS Devices', permission: 'gpsdevice.list' },
    { id: 'areas', label: 'Areas', permission: 'area.list' },
    { id: 'tariff', label: 'Tariff', permission: 'tariff.list' },
  ];

  const staffSubTabs = [
    { id: 'staff-categories', label: 'Staff Categories', permission: 'staffcategory.list' },
    { id: 'roles', label: 'Roles', permission: 'role.list' },
  ];

  const patientSubTabs = [
    { id: 'patient-vital-fields', label: 'Patient Vital Fields', permission: 'patientvitalfield.list' },
  ];

  const tenantSubTabs = [
    { id: 'tenant-settings', label: 'Tenant Settings', permission: 'tenant.update' },
    { id: 'config-settings', label: 'Config Settings', permission: 'tenant.update' },
    { id: 'permissions', label: 'Permissions', permission: 'permission.list' },
    { id: 'profile', label: 'Profile', permission: 'profile.view' },
    ...(isDevMode ? [{ id: 'login-page-config', label: 'Login Page Config', permission: 'tenant.update' }] : []),
  ];

  const bookingSubTabs = [
    { id: 'cancel-reason', label: 'Cancel Reasons', permission: 'cancelreason.list' },
  ];

  const getSubTabs = () => {
    switch (mainTab) {
      case 'ambulance':
        return ambulanceSubTabs;
      case 'staff':
        return staffSubTabs;
      case 'patient':
        return patientSubTabs;
      case 'tenant':
        return tenantSubTabs;
      case 'booking':
        return bookingSubTabs;
      default:
        return [];
    }
  };

  const renderSubTabContent = () => {
    switch (subTab) {
      case 'ambulance-table':
        return <AmbulanceManagement skipLayout={true} hideAmbulanceTab={true} />;
      case 'ambulance-type':
        return <AmbulanceTypeManagement skipLayout={true} />;
      case 'gps-devices':
        return <GpsDeviceManagement skipLayout={true} />;
      case 'areas':
        return <AreaManagement skipLayout={true} />;
      case 'staff-categories':
        return <StaffCategoryManagement skipLayout={true} />;
      case 'roles':
        return <RoleManagement skipLayout={true} />;
      case 'patient-vital-fields':
        return <PatientVitalFieldsManagement skipLayout={true} />;
      case 'tenant-settings':
        return <TenantSettings skipLayout={true} />;
      case 'config-settings':
        return <ConfigSettings skipLayout={true} />;
      case 'permissions':
        return <PermissionManagement skipLayout={true} />;
      case 'profile':
        return <Profile skipLayout={true} />;
      case 'login-page-config':
        return <LoginPageConfig skipLayout={true} />;
      case 'cancel-reason':
        return <CancelReasonManagement skipLayout={true} />;
      case 'tariff':
        return <TariffManagement skipLayout={true} />;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      {/* <div className="page-header">
        <h2>Settings</h2>
      </div> */}

      {/* Main Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        borderBottom: `2px solid ${isDark ? '#334155' : '#e2e8f0'}`,
        marginBottom: '24px',
        flexWrap: 'wrap',
      }}>
        <button
          onClick={() => {
            setMainTab('ambulance');
            setSubTab('ambulance-table');
          }}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            color: mainTab === 'ambulance' 
              ? (isDark ? '#3b82f6' : '#2563eb')
              : (isDark ? '#94a3b8' : '#64748b'),
            borderBottom: mainTab === 'ambulance' 
              ? `2px solid ${isDark ? '#3b82f6' : '#2563eb'}`
              : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: mainTab === 'ambulance' ? '600' : '400',
            marginBottom: '-2px',
            transition: 'all 0.2s',
          }}
        >
          Ambulance
        </button>
        <button
          onClick={() => {
            setMainTab('staff');
            setSubTab('staff-categories');
          }}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            color: mainTab === 'staff' 
              ? (isDark ? '#3b82f6' : '#2563eb')
              : (isDark ? '#94a3b8' : '#64748b'),
            borderBottom: mainTab === 'staff' 
              ? `2px solid ${isDark ? '#3b82f6' : '#2563eb'}`
              : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: mainTab === 'staff' ? '600' : '400',
            marginBottom: '-2px',
            transition: 'all 0.2s',
          }}
        >
          Staff
        </button>
        <button
          onClick={() => {
            setMainTab('patient');
            setSubTab('patient-vital-fields');
          }}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            color: mainTab === 'patient' 
              ? (isDark ? '#3b82f6' : '#2563eb')
              : (isDark ? '#94a3b8' : '#64748b'),
            borderBottom: mainTab === 'patient' 
              ? `2px solid ${isDark ? '#3b82f6' : '#2563eb'}`
              : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: mainTab === 'patient' ? '600' : '400',
            marginBottom: '-2px',
            transition: 'all 0.2s',
          }}
        >
          Patient
        </button>
        {canSeeTenantTab && (
          <button
            onClick={() => {
              setMainTab('tenant');
              setSubTab('tenant-settings');
            }}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'transparent',
              color: mainTab === 'tenant' 
                ? (isDark ? '#3b82f6' : '#2563eb')
                : (isDark ? '#94a3b8' : '#64748b'),
              borderBottom: mainTab === 'tenant' 
                ? `2px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: mainTab === 'tenant' ? '600' : '400',
              marginBottom: '-2px',
              transition: 'all 0.2s',
            }}
          >
            Tenant
          </button>
        )}
        <button
          onClick={() => {
            setMainTab('booking');
            setSubTab('cancel-reason');
          }}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            color: mainTab === 'booking' 
              ? (isDark ? '#3b82f6' : '#2563eb')
              : (isDark ? '#94a3b8' : '#64748b'),
            borderBottom: mainTab === 'booking' 
              ? `2px solid ${isDark ? '#3b82f6' : '#2563eb'}`
              : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: mainTab === 'booking' ? '600' : '400',
            marginBottom: '-2px',
            transition: 'all 0.2s',
          }}
        >
          Booking
        </button>
      </div>

      {/* Sub Tabs */}
      {getSubTabs().length > 0 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          marginBottom: '24px',
          flexWrap: 'wrap',
        }}>
          {getSubTabs().map((tab) => {
            if (!hasPermission(tab.permission)) {
              return null;
            }
            // Hide login-page-config if not in dev mode
            if (tab.id === 'login-page-config' && !isDevMode) return null;
            return (
              <button
                key={tab.id}
                onClick={() => setSubTab(tab.id)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: 'transparent',
                  color: subTab === tab.id 
                    ? (isDark ? '#3b82f6' : '#2563eb')
                    : (isDark ? '#94a3b8' : '#64748b'),
                  borderBottom: subTab === tab.id 
                    ? `2px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                    : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: subTab === tab.id ? '600' : '400',
                  marginBottom: '-1px',
                  transition: 'all 0.2s',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      <div>
        {renderSubTabContent()}
      </div>
    </DashboardLayout>
  );
};

export default Settings;
